import React, { useEffect, useState } from "react";
import {
  ChevronDown,
  ChevronUp,
  MoreVertical,
  Package,
  Pencil,
  Plus,
  Power,
  Search,
  SlidersHorizontal,
  Trash2,
  Upload,
  X,
} from "lucide-react";
import { useNavigate } from "react-router-dom";

import DashboardLayout from "src/components/layout/dashboard/Layout";
import TableV2 from "src/components/ui/TableV2";
import { Badge } from "src/components/ui/Badge";
import CardHeader from "src/components/common/card/CardHeader";
import Button from "src/components/ui/Button";
import Input from "src/components/ui/Input";
import FilterSelect from "src/components/ui/FilterSelect";
import PageSizeSelect from "src/components/ui/PageSizeSelect";
import Card from "src/components/common/card/Card";
import Popover from "src/components/ui/Popover";
import ConfirmationModal from "src/components/modals/ConfirmationModal";
import BulkImportModal, {
  BulkImportColumn,
} from "src/components/bulk-import/BulkImportModal";

import { useAppDispatch, useAppSelector } from "src/redux/hooks";
import * as productActions from "src/redux/slices/productSlice";
import * as categoryActions from "src/redux/slices/categorySlice";
import * as brandActions from "src/redux/slices/brandSlice";
import * as unitActions from "src/redux/slices/unitSlice";
import { Product } from "src/types/product.types";
import { toast } from "react-toastify";
import { currencyFormatter } from "src/utils/common";
import usePlanUsage from "src/hooks/usePlanUsage";
import productApis from "src/api/product";

const PRODUCT_IMPORT_COLUMNS: BulkImportColumn[] = [
  { key: "name", label: "Name", required: true, example: "Wireless Mouse" },
  {
    key: "categoryName",
    label: "Category",
    required: true,
    example: "Electronics",
    aliases: ["category"],
  },
  {
    key: "subCategoryName",
    label: "Sub Category",
    example: "Accessories",
    aliases: ["subcategory", "sub category"],
  },
  { key: "brandName", label: "Brand", example: "Logitech", aliases: ["brand"] },
  { key: "unitName", label: "Unit", example: "Piece", aliases: ["unit"] },
  { key: "sku", label: "SKU", example: "WM-001" },
  { key: "barcode", label: "Barcode", example: "8901234567890" },
  {
    key: "sellingPrice",
    label: "Selling Price",
    required: true,
    example: 499,
  },
  { key: "mrp", label: "MRP", example: 599 },
  { key: "buyingPrice", label: "Buying Price", example: 350 },
  { key: "minStock", label: "Min Stock", example: 10 },
  { key: "gstRate", label: "GST Rate", example: 18 },
  { key: "hsnCode", label: "HSN Code", example: "8471" },
  { key: "description", label: "Description", example: "Ergonomic wireless mouse" },
  { key: "status", label: "Status", example: "active" },
  {
    key: "variantOption1Name",
    label: "Variant Option 1 Name",
    example: "",
    aliases: ["variant option 1 name", "option 1 name"],
  },
  {
    key: "variantOption1Value",
    label: "Variant Option 1 Value",
    example: "",
    aliases: ["variant option 1 value", "option 1 value"],
  },
  {
    key: "variantOption2Name",
    label: "Variant Option 2 Name",
    example: "",
    aliases: ["variant option 2 name", "option 2 name"],
  },
  {
    key: "variantOption2Value",
    label: "Variant Option 2 Value",
    example: "",
    aliases: ["variant option 2 value", "option 2 value"],
  },
  {
    key: "variantOption3Name",
    label: "Variant Option 3 Name",
    example: "",
    aliases: ["variant option 3 name", "option 3 name"],
  },
  {
    key: "variantOption3Value",
    label: "Variant Option 3 Value",
    example: "",
    aliases: ["variant option 3 value", "option 3 value"],
  },
];

// Row 1 is a plain single product. Rows 2-3 share the Name "Cotton
// T-Shirt" — that's how a product with variants is represented: same
// Name repeated across rows, with the variant-option columns and price
// differing per row. See BulkImportModal's grouping logic.
const PRODUCT_IMPORT_SAMPLE_ROWS: Array<Record<string, string | number>> = [
  {
    name: "Wireless Mouse",
    categoryName: "Electronics",
    brandName: "Logitech",
    unitName: "Piece",
    sku: "WM-001",
    barcode: "8901234567890",
    sellingPrice: 499,
    mrp: 599,
    buyingPrice: 350,
    minStock: 10,
    gstRate: 18,
    hsnCode: "8471",
    description: "Ergonomic wireless mouse",
    status: "active",
  },
  {
    name: "Cotton T-Shirt",
    categoryName: "Apparel",
    unitName: "Piece",
    sku: "TS-S-RED",
    sellingPrice: 399,
    mrp: 499,
    gstRate: 5,
    status: "active",
    variantOption1Name: "Size",
    variantOption1Value: "S",
    variantOption2Name: "Color",
    variantOption2Value: "Red",
  },
  {
    name: "Cotton T-Shirt",
    categoryName: "Apparel",
    unitName: "Piece",
    sku: "TS-M-RED",
    sellingPrice: 399,
    mrp: 499,
    gstRate: 5,
    status: "active",
    variantOption1Name: "Size",
    variantOption1Value: "M",
    variantOption2Name: "Color",
    variantOption2Value: "Red",
  },
];

const STATUS_OPTIONS = [
  { label: "Active", value: "active" },
  { label: "Inactive", value: "inactive" },
];

const ProductListPage: React.FC = () => {
  const [limit, setLimit] = useState(10);
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [showImportModal, setShowImportModal] = useState(false);
  const [showStatusModal, setShowStatusModal] = useState(false);
  const [statusRow, setStatusRow] = useState<Product | null>(null);
  const [statusUpdate, setStatusUpdate] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteRow, setDeleteRow] = useState<Product | null>(null);
  const [deleteRequested, setDeleteRequested] = useState(false);
  const [searchInput, setSearchInput] = useState("");
  const [search, setSearch] = useState("");
  const [categoryId, setCategoryId] = useState("");
  const [brandId, setBrandId] = useState("");
  const [unitId, setUnitId] = useState("");
  const [status, setStatus] = useState<"" | "active" | "inactive">("");

  const { productList, editProduct, deleteProduct } = useAppSelector(
    (state) => state.product
  );
  const { categoryList } = useAppSelector((state) => state.category);
  const { brandList } = useAppSelector((state) => state.brand);
  const { unitList } = useAppSelector((state) => state.unit);
  const {
    user: { data: user },
  } = useAppSelector((state) => state.authTenant);
  const permissions = user?.roleDetails?.permissions || user?.permissions || {};
  const isTenantAdmin =
    user?.role === "admin" ||
    user?.roleDetails?.name?.toLowerCase() === "admin";
  const canCreateProduct =
    isTenantAdmin || (permissions?.products || []).includes("create");
  const canEditProduct =
    isTenantAdmin || (permissions?.products || []).includes("edit");
  const canToggleProduct =
    isTenantAdmin || (permissions?.products || []).includes("delete");
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { planUsage } = usePlanUsage();

  const productLimit = planUsage?.plan?.features?.totalProducts;
  const productUsage = planUsage?.usage?.totalProducts;
  const isPlanExpired = planUsage?.planStatus === "expired";
  const isProductLimitReached =
    typeof productLimit === "number" &&
    typeof productUsage === "number" &&
    productUsage >= productLimit;
  const disableCreateProduct = isPlanExpired || isProductLimitReached;

  const rows: Product[] = productList?.data?.data || [];

  useEffect(() => {
    dispatch(categoryActions.getCategoryListRequest({ page: 1, limit: 100 }));
    dispatch(brandActions.getBrandListRequest({ page: 1, limit: 100 }));
    dispatch(unitActions.getUnitListRequest({ page: 1, limit: 100 }));
  }, [dispatch]);

  // Debounce the search box before it hits the API/filters
  useEffect(() => {
    const timer = setTimeout(() => setSearch(searchInput.trim()), 350);
    return () => clearTimeout(timer);
  }, [searchInput]);

  useEffect(() => {
    getProductList(1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search, categoryId, brandId, unitId, status, limit]);

  // ✅ Status toggle side-effects
  useEffect(() => {
    if (editProduct.data && statusUpdate) {
      toast.success("Product modified successfully");
      dispatch(productActions.resetEditProduct());
      setStatusUpdate(false);
      handleStatusModalClose();
      getProductList(productList?.data?.pagination?.currentPage || 1);
    } else if (editProduct.error && statusUpdate) {
      toast.error(`Error: ${editProduct.error}`);
      setStatusUpdate(false);
      handleStatusModalClose();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editProduct, dispatch, statusUpdate]);

  // ✅ Delete side-effects
  useEffect(() => {
    if (deleteProduct.data && deleteRequested) {
      toast.success("Product deleted successfully");
      dispatch(productActions.resetDeleteProduct());
      setDeleteRequested(false);
      handleDeleteModalClose();
      getProductList(1);
    } else if (deleteProduct.error && deleteRequested) {
      toast.error(`Error: ${deleteProduct.error}`);
      setDeleteRequested(false);
      handleDeleteModalClose();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [deleteProduct, dispatch, deleteRequested]);

  const getProductList = (page: number) =>
    dispatch(
      productActions.getProductListRequest({
        page,
        limit,
        ...(search ? { search } : {}),
        ...(categoryId ? { categoryId } : {}),
        ...(brandId ? { brandId } : {}),
        ...(unitId ? { unitId } : {}),
        ...(status ? { status } : {}),
      })
    );

  const handlePageChange = (page: number) => {
    getProductList(page);
  };

  const handleClearFilters = () => {
    setSearchInput("");
    setSearch("");
    setCategoryId("");
    setBrandId("");
    setUnitId("");
    setStatus("");
  };

  const hasActiveFilters = !!(
    search ||
    categoryId ||
    brandId ||
    unitId ||
    status
  );

  const handleStatusUpdate = (_id: string, nextStatus: "active" | "inactive") => {
    dispatch(productActions.editProductRequest({ _id, status: nextStatus }));
  };

  const handleStatusModalClose = () => {
    setShowStatusModal(false);
    setStatusRow(null);
  };

  const onConfirmStatus = () => {
    if (!statusRow?._id) return;
    setStatusUpdate(true);
    handleStatusUpdate(
      statusRow._id,
      statusRow.status === "active" ? "inactive" : "active"
    );
  };

  const handleDeleteModalClose = () => {
    setShowDeleteModal(false);
    setDeleteRow(null);
  };

  const onConfirmDelete = () => {
    if (!deleteRow?._id) return;
    setDeleteRequested(true);
    dispatch(productActions.deleteProductRequest(deleteRow._id));
  };

  const handleEdit = (data: Product) => {
    navigate(`/app/product/${data._id}/edit`, { state: { product: data } });
  };

  return (
    <DashboardLayout
      meta={{
        title: "Products | Invyntra",
        description:
          "Track and manage all product plans with detailed customer and pricing information.",
        keywords: "plans, products, invoices, inventory, invyntra",
      }}
    >
      <div className="space-y-4">
        {/* Page header */}
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div className="flex items-start gap-4">
            <div className="flex h-14 w-14 shrink-0 items-center justify-center rounded-2xl bg-primary-100 text-primary-600 dark:bg-primary-900/40 dark:text-primary-300">
              <Package size={26} />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100">
                Products
              </h1>
              <p className="text-sm text-gray-500 dark:text-gray-400">
                Manage your product catalog.
              </p>
            </div>
          </div>

          <div className="flex items-center gap-3">
            {canCreateProduct && (
              <Button
                variant="outline"
                rounded="full"
                onClick={() => setShowImportModal(true)}
              >
                <Upload size={16} className="mr-1.5" />
                Bulk import
              </Button>
            )}
            {canCreateProduct && (
              <Button
                rounded="full"
                onClick={() => navigate("/app/product/add")}
                disabled={disableCreateProduct}
                title={
                  isPlanExpired
                    ? "Plan expired. Please upgrade."
                    : isProductLimitReached
                    ? "Product limit reached. Please upgrade."
                    : undefined
                }
              >
                <Plus size={16} className="mr-1" />
                <span>{disableCreateProduct ? "Limit Reached" : "Add Product"}</span>
              </Button>
            )}
          </div>
        </div>

        {/* Filters */}
        <Card>
          <div className="flex w-full items-center justify-between">
            <span className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">
              <SlidersHorizontal size={14} />
              Filters
              {hasActiveFilters && (
                <span className="h-1.5 w-1.5 rounded-full bg-primary-500" />
              )}
            </span>
            <Button
              variant="outline"
              size="sm"
              rounded="full"
              onClick={() => setFiltersOpen((o) => !o)}
            >
              {filtersOpen ? "Hide filters" : "Show filters"}
              {filtersOpen ? (
                <ChevronUp size={16} className="ml-1.5" />
              ) : (
                <ChevronDown size={16} className="ml-1.5" />
              )}
            </Button>
          </div>

          {filtersOpen && (
            <div className="mt-4 grid grid-cols-1 gap-4 border-t border-gray-100 pt-4 dark:border-gray-700 sm:grid-cols-2 lg:grid-cols-6">
              <div>
                <label className="mb-1 block text-sm font-medium text-gray-700 dark:text-gray-300">
                  Search
                </label>
                <Input
                  placeholder="Search products..."
                  value={searchInput}
                  onChange={(e) => setSearchInput(e.target.value)}
                  startAdornment={<Search size={14} />}
                />
              </div>

              <FilterSelect
                variant="boxed"
                label="Category"
                value={categoryId}
                onChange={setCategoryId}
                allLabel="All categories"
                options={(categoryList?.data?.data || []).map((c: any) => ({
                  label: c.name,
                  value: c._id,
                }))}
              />

              <FilterSelect
                variant="boxed"
                label="Brand"
                value={brandId}
                onChange={setBrandId}
                allLabel="All brands"
                options={(brandList?.data?.data || []).map((b: any) => ({
                  label: b.name,
                  value: b._id,
                }))}
              />

              <FilterSelect
                variant="boxed"
                label="Unit"
                value={unitId}
                onChange={setUnitId}
                allLabel="All units"
                options={(unitList?.data?.data || []).map((u: any) => ({
                  label: u.name,
                  value: u._id,
                }))}
              />

              <FilterSelect
                variant="boxed"
                label="Status"
                value={status}
                onChange={(val) => setStatus(val as "" | "active" | "inactive")}
                allLabel="All statuses"
                options={STATUS_OPTIONS}
              />

              <div className="flex items-end">
                <Button
                  variant="outline"
                  color="danger"
                  rounded="full"
                  fullWidth
                  onClick={handleClearFilters}
                  disabled={!hasActiveFilters}
                >
                  <X size={15} className="mr-1.5" />
                  Clear filters
                </Button>
              </div>
            </div>
          )}
        </Card>

        {/* Product list */}
        <Card>
          <CardHeader
            title="Product list"
            description="Search and manage items in your catalog."
            actions={<PageSizeSelect value={limit} onChange={setLimit} />}
          />

          <TableV2
            columns={[
              {
                key: "productId",
                header: "#ID",
                accessor: ({ key, row }) => (
                  <span className="text-gray-500">#{row[key]}</span>
                ),
              },
              {
                key: "name",
                header: "Product",
                width: "220px",
                accessor: ({ key, row }) =>
                  canEditProduct ? (
                    <div>
                      <button
                        type="button"
                        onClick={() => handleEdit(row)}
                        title={row[key]}
                        className="block max-w-full truncate text-left font-medium text-primary-700 hover:underline dark:text-primary-400"
                      >
                        {row[key]}
                      </button>
                      {row?.sku && (
                        <p className="truncate text-xs text-gray-500 dark:text-gray-400">
                          SKU: {row?.sku}
                        </p>
                      )}
                    </div>
                  ) : (
                    <div>
                      <div
                        title={row[key]}
                        className="truncate font-medium text-gray-900 dark:text-gray-100"
                      >
                        {row[key]}
                      </div>
                      {row?.sku && (
                        <p className="truncate text-xs text-gray-500 dark:text-gray-400">
                          SKU: {row?.sku}
                        </p>
                      )}
                    </div>
                  ),
              },
              {
                key: "category",
                header: "Category",
                accessor: ({ row }) => (
                  <div>
                    <div>{row?.categoryId?.name || "-"}</div>
                    {row?.subCategoryId?.name && (
                      <div className="text-xs text-gray-400">
                        / {row?.subCategoryId?.name}
                      </div>
                    )}
                  </div>
                ),
              },
              {
                key: "buyingPrice",
                header: "Cost",
                accessor: ({ row }) =>
                  row.buyingPrice ? (
                    <span className="text-gray-700 dark:text-gray-300">
                      {currencyFormatter(row.buyingPrice)}
                    </span>
                  ) : (
                    <span className="text-gray-400 dark:text-gray-500">-</span>
                  ),
              },
              {
                key: "sellingPrice",
                header: "Price",
                accessor: ({ row }) => (
                  <div>
                    <div className="font-semibold text-primary-700 dark:text-primary-400">
                      {currencyFormatter(row.sellingPrice)}
                    </div>
                    {row.mrp && row.mrp !== row.sellingPrice && (
                      <div className="text-xs text-gray-400 line-through">
                        {currencyFormatter(row.mrp)}
                      </div>
                    )}
                  </div>
                ),
              },
              {
                key: "variants",
                header: "Variants",
                accessor: ({ row }) =>
                  row.productType === "variant" ? (
                    <span className="text-gray-700 dark:text-gray-300">
                      {row.variants?.length || 0} variant
                      {row.variants?.length === 1 ? "" : "s"}
                    </span>
                  ) : (
                    <span className="text-gray-400 dark:text-gray-500">-</span>
                  ),
              },
              {
                key: "status",
                header: "Status",
                accessor: ({ key, row }) => (
                  <Badge
                    text={row?.[key] === "active" ? "Active" : "Inactive"}
                    color={row?.[key] === "active" ? "green" : "gray"}
                  />
                ),
              },
              {
                key: "actions",
                header: "Action",
                accessor: ({ row }) =>
                  !canEditProduct && !canToggleProduct ? null : (
                    <Popover
                      trigger={
                        <button
                          className="p-2 rounded-full text-gray-500 hover:bg-gray-100 hover:text-gray-700 transition dark:text-gray-400 dark:hover:bg-gray-700 dark:hover:text-gray-100"
                          title="Actions"
                        >
                          <MoreVertical size={16} />
                        </button>
                      }
                    >
                      {(close) => (
                        <div className="w-44 py-1.5">
                          {canEditProduct && (
                            <button
                              className="flex w-full items-center gap-2.5 px-3 py-2 text-sm text-gray-700 hover:bg-gray-50 dark:text-gray-200 dark:hover:bg-gray-700"
                              onClick={() => {
                                close();
                                handleEdit(row);
                              }}
                            >
                              <Pencil size={14} />
                              Edit
                            </button>
                          )}
                          {canToggleProduct && (
                            <button
                              className="flex w-full items-center gap-2.5 px-3 py-2 text-sm text-gray-700 hover:bg-gray-50 dark:text-gray-200 dark:hover:bg-gray-700"
                              onClick={() => {
                                close();
                                setStatusRow(row);
                                setShowStatusModal(true);
                              }}
                            >
                              <Power size={14} />
                              {row.status === "active" ? "Deactivate" : "Activate"}
                            </button>
                          )}
                          {canToggleProduct && (
                            <button
                              className="flex w-full items-center gap-2.5 px-3 py-2 text-sm text-red-600 hover:bg-red-50 dark:text-red-400 dark:hover:bg-red-900/30"
                              onClick={() => {
                                close();
                                setDeleteRow(row);
                                setShowDeleteModal(true);
                              }}
                            >
                              <Trash2 size={14} />
                              Delete
                            </button>
                          )}
                        </div>
                      )}
                    </Popover>
                  ),
              },
            ]}
            data={rows}
            pagination={productList?.data?.pagination}
            onPageChange={handlePageChange}
            loading={productList.loading}
          />
        </Card>
      </div>

      {showImportModal && (
        <BulkImportModal
          open={showImportModal}
          onClose={() => setShowImportModal(false)}
          title="Bulk import products"
          entityLabel="products"
          columns={PRODUCT_IMPORT_COLUMNS}
          sampleRows={PRODUCT_IMPORT_SAMPLE_ROWS}
          sampleFileName="product-import-sample.xlsx"
          onImport={(rows) => productApis.bulkImport(rows)}
          onImported={() => getProductList(1)}
          helperNote={
            <>
              To import a product with multiple variants (e.g. Size, Color),
              repeat the same <strong>Name</strong> across multiple rows and
              fill in the Variant Option columns differently per row — see
              the sample sheet for an example.
            </>
          }
        />
      )}

      {showStatusModal && statusRow?._id && (
        <ConfirmationModal
          message={`Are you sure to ${
            statusRow.status === "active" ? "deactivate" : "activate"
          } this product?`}
          onConfirm={onConfirmStatus}
          onCancel={handleStatusModalClose}
          open={showStatusModal}
          isLoading={editProduct.loading}
        />
      )}

      {showDeleteModal && deleteRow?._id && (
        <ConfirmationModal
          title="Delete product"
          message={
            <>
              Are you sure you want to delete{" "}
              <span className="font-semibold text-gray-900 dark:text-gray-100">
                {deleteRow.name}
              </span>
              ? This action cannot be undone.
            </>
          }
          variant="danger"
          confirmLabel="Delete"
          onConfirm={onConfirmDelete}
          onCancel={handleDeleteModalClose}
          open={showDeleteModal}
          isLoading={deleteProduct.loading}
        />
      )}
    </DashboardLayout>
  );
};

export default ProductListPage;
